import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { usePageSeo } from '../hooks/usePageSeo';
import { absoluteUrl } from '../utils/seo';
import WaybackLink from '../components/WaybackLink';
import { DEFAULT_HALL_OF_FAME_ENTRIES, type HallOfFameEntry } from '../data/hallOfFameEntries';

interface Props {
  darkMode: boolean;
}

const HallOfFamePage: React.FC<Props> = ({ darkMode }) => {
  const [entries, setEntries] = useState<HallOfFameEntry[]>(DEFAULT_HALL_OF_FAME_ENTRIES);
  const [loading, setLoading] = useState(true);

  usePageSeo({
    title: 'Forum Hall of Fame - Efsane Kapanan Siteler',
    description: 'Türk internet tarihinde iz bırakan kapanmış forum siteleri.',
    keywords: 'forum hall of fame, kapanan forumlar',
    canonical: absoluteUrl('/hall-of-fame'),
  });

  useEffect(() => {
    supabase
      .from('hall_of_fame_entries')
      .select('id, slug, name, story, category, closed_at, peak_members, peak_posts, wayback_url')
      .eq('is_published', true)
      .order('sort_order', { ascending: true })
      .then(({ data, error }) => {
        if (!error && data && data.length > 0) {
          setEntries(data as HallOfFameEntry[]);
        }
        setLoading(false);
      });
  }, []);

  const card = darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200 shadow-sm';
  const muted = darkMode ? 'text-gray-400' : 'text-gray-600';
  const body = darkMode ? 'text-gray-300' : 'text-gray-700';
  const title = darkMode ? 'text-white' : 'text-gray-900';

  return (
    <div className={`min-h-screen overflow-x-hidden ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-8 max-w-3xl">
        <h1 className={`text-2xl sm:text-3xl font-bold mb-2 ${title}`}>Forum Hall of Fame</h1>
        <p className={`mb-8 ${muted}`}>
          Türk internetinde iz bırakan, kapanmış veya arşive kavuşmuş efsane forum toplulukları.
        </p>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500" />
          </div>
        ) : entries.length === 0 ? (
          <p className={`rounded-xl border p-6 text-center ${card} ${muted}`}>
            Henüz listelenecek forum bulunmuyor.
          </p>
        ) : (
          <div className="space-y-6">
            {entries.map((e) => (
              <article key={e.id} className={`rounded-xl border p-4 sm:p-6 ${card}`}>
                <h2 className={`text-lg sm:text-xl font-bold break-words ${title}`}>{e.name}</h2>
                {e.category && <p className={`text-sm mt-1 ${muted}`}>{e.category}</p>}
                {e.story && <p className={`mt-3 leading-relaxed ${body}`}>{e.story}</p>}
                <dl className={`mt-4 flex flex-wrap gap-4 text-sm ${muted}`}>
                  {e.peak_members != null && (
                    <div>
                      <dt className="inline">Zirve üye: </dt>
                      <dd className={`inline font-semibold tabular-nums ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                        {e.peak_members.toLocaleString('tr-TR')}
                      </dd>
                    </div>
                  )}
                  {e.peak_posts != null && (
                    <div>
                      <dt className="inline">Zirve ileti: </dt>
                      <dd className={`inline font-semibold tabular-nums ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                        {e.peak_posts.toLocaleString('tr-TR')}
                      </dd>
                    </div>
                  )}
                  {e.closed_at && (
                    <div>
                      <dt className="inline">Kapanış: </dt>
                      <dd className={`inline ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                        {new Date(e.closed_at).toLocaleDateString('tr-TR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </dd>
                    </div>
                  )}
                </dl>
                <div className="mt-4 flex flex-wrap gap-4">
                  <WaybackLink waybackUrl={e.wayback_url} name={e.name} darkMode={darkMode} />
                  <Link to="/inactive" className="text-sm text-indigo-500 hover:underline">
                    Forum mezarlığı
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HallOfFamePage;
